
    import __i18nConfig from '@next-translate-root/i18n'
    import __loadNamespaces from 'next-translate/loadNamespaces'
    import React, { useState, useEffect } from 'react';
import styled from '@emotion/styled';
import Link from 'next/link';
import useTranslation from 'next-translate/useTranslation';
import { css } from '@emotion/react';
import { OutletPresenter } from 'shared/presenters';
import { ShowOnMarketplace } from 'shared/library/atoms/ConditionalMarketplace';
import Flex from 'shared/library/atoms/Flex';
import { H2 } from 'library/atoms/Typography';
import Icon from 'shared/library/atoms/Icon';
import Pin from 'library/molecules/Pin';
import Rating from 'library/molecules/Rating';
import { ReviewCard } from 'library/organisms/ReviewsCarousel';
import { Text } from 'library/atoms/Typography';
const Sidebar = ({ outlet }: {
    outlet: OutletPresenter;
}) => {
    const pinsToDisplay = 4;
    const [showAllPins, setShowAllPins] = useState((outlet?.active_schedulables?.length || 0) <= pinsToDisplay);
    const [inIframe, setInIframe] = useState(true);
    const { t } = useTranslation('common');
    useEffect(() => {
        try {
            setInIframe(window.self !== window.top);
        }
        catch (e) {
            setInIframe(true); // Assume iframe if access is denied
        }
    });
    // If the page is loaded in an iframe, we don't want to show the sidebar
    // as it is likely part of the outlet page which has this information already
    if (inIframe) {
        return <></>;
    }
    return (<>
      <SidebarFlex flexDirection="column" alignItems="flex-start">
        <Flex flexDirection="column" gap={1} alignItems="flex-start">
          <H2>{outlet.name}</H2>
          <FlipOnMobile>
            <ShowOnMarketplace>
              {outlet.description() && (<TruncatedText as="div" color="secondary" fontStyle="p2" dangerouslySetInnerHTML={{
                __html: outlet.description()
            }}/>)}
            </ShowOnMarketplace>
            <TagContainer gap={2}>
              {outlet.active_schedulables &&
            outlet.active_schedulables.map((schedulable, index) => (<TogglablePin icon={schedulable?.activity_type?.css_class} key={schedulable?.activity_type?.name} display={showAllPins || index <= pinsToDisplay - 1}>
                    <Text fontStyle="p4" color="secondary">
                      {schedulable?.activity_type?.name}
                    </Text>
                  </TogglablePin>))}
              {!showAllPins && (<a onClick={() => {
                setShowAllPins(true);
            }}>
                  <Text fontStyle="p3" color="secondary">
                    {t('common:show-all-activities')}
                  </Text>
                </a>)}
            </TagContainer>
          </FlipOnMobile>
        </Flex>

        <MobileFlex flexDirection="row" gap={2} alignItems="flex-start">
          {outlet.googleMapsURL() && (<LocationFlex flexDirection="row" gap={2} alignItems="center">
              <StyledFlexLink href={outlet.googleMapsURL() || ''}>
                <Icon name="pin" size={17}/>
                <Text fontStyle="p2" color="secondary">
                  {outlet.address?.city}
                  {outlet.address?.state ? `, ${outlet.address?.state}` : ''}
                </Text>
                <Icon name="arrow-right" size={16}/>
              </StyledFlexLink>
            </LocationFlex>)}

          {outlet.ratings?.[0]?.score && outlet.ratings?.[0]?.url && (<Flex flexDirection="row" gap={1} alignItems="center" justifyContent="center">
              <StyledFlexLink href={outlet.ratings?.[0]?.url} target="_blank">
                <Rating score={outlet.ratings?.[0]?.score} maxScore={5} size={18} starColor="gold"/>
                <Text color="secondary">{outlet.ratings?.[0]?.score}</Text>
                <Icon name="arrow-right" size={16}/>
              </StyledFlexLink>
            </Flex>)}
        </MobileFlex>

        <DesktopFlex flexDirection="column" gap={3} alignItems="flex-start">
          <Flex flexDirection="row" gap={2}>
            <Icon name="pin" size={32}/>
            <Text fontStyle="p2" color="secondary">
              {outlet.address?.building_name
            ? `${outlet.address.building_name} `
            : ''}
              {outlet.address?.street_name}
              <br />
              {outlet.address?.city}
              <br />
              {outlet.address?.post_code ? `${outlet.address.post_code}` : ''}
              {outlet.address?.state ? `, ${outlet.address.state}` : ''}
            </Text>
          </Flex>
          {outlet.googleMapsURL() && (<MapContainer>
              <a href={outlet.googleMapsURL()}>
                <picture>
                  <MapImage src={outlet.mapboxImageURL({
                width: 360,
                height: 200,
                zoom: 9
            })}/>
                </picture>
              </a>
            </MapContainer>)}
        </DesktopFlex>
        {outlet.reviews &&
            outlet.ratings?.[0]?.score &&
            outlet.ratings?.[0]?.url && (<DesktopFlex flexDirection="column" gap={3} alignItems="flex-start">
              <LinelessLink href={outlet.ratings?.[0]?.url}>
                <Flex flexDirection="row" gap={1} alignItems="center" justifyContent="center">
                  <Rating score={outlet.ratings?.[0]?.score} maxScore={5} size={18} starColor="gold"/>
                  <Text color="secondary">{outlet.ratings?.[0]?.score}</Text>
                  <Icon name="arrow-right" size={16}/>
                </Flex>
              </LinelessLink>
              <Text fontStyle="p2" color="secondary">
                {t('common:reviews-tagline', {
                outletName: outlet.name
            })}
              </Text>
              {outlet.reviews.map(review => {
                return (<ReviewCard key={`review-${review.id}`} review={review} starColor="gold"/>);
            })}
              {outlet.ratings?.[0]?.url && (<LinelessLink href={outlet.ratings?.[0]?.url} target="_blank">
                  <Text fontStyle="p2" color="secondary">
                    {t('common:read-more-reviews')}{' '}
                    <Icon name="arrow-right" size={12}/>
                  </Text>
                </LinelessLink>)}
            </DesktopFlex>)}
      </SidebarFlex>
    </>);
};
export default Sidebar;
const DesktopFlex = styled(Flex)(({ theme }) => ({
    display: 'none',
    [theme.mediaQueries.lg]: {
        display: 'flex'
    }
}));
const MobileFlex = styled(Flex)(({ theme }) => ({
    width: '100%',
    gap: theme.space[2],
    flexDirection: 'column',
    [theme.mediaQueries.md]: {
        gap: theme.space[8],
        flexDirection: 'row'
    },
    [theme.mediaQueries.lg]: {
        display: 'none'
    }
}));
const FlipOnMobile = styled(Flex)(({ theme }) => ({
    flexDirection: 'column-reverse',
    gap: theme.space[2],
    [theme.mediaQueries.lg]: {
        flexDirection: 'column'
    }
}));
const SidebarFlex = styled(Flex)(({ theme }) => ({
    gap: theme.space[4],
    [theme.mediaQueries.lg]: {
        gap: theme.space[7]
    }
}));
const TogglablePin = styled(Pin)<{
    display?: boolean;
}>(({ display }) => ({
    display: display ? 'inline-flex' : 'none'
}));
const TruncatedText = styled(Text)(({ theme }) => ({
    ...theme.mixins.ellipsisLineClamp(6)
}));
const LocationFlex = styled(Flex)(({ theme }) => ({
    ...theme.mixins.ellipsisLineClamp(2)
}));
const StyledFlexLink = styled(Link)(({ theme }) => ({
    display: 'flex',
    textDecoration: 'none',
    alignItems: 'center',
    gap: theme.space[1]
}));
const LinelessLink = styled(Link)(() => ({
    textDecoration: 'none'
}));
const TagContainer = styled(Flex)(() => css({
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: 'center'
}));
const MapImage = styled.img(() => ({
    width: '100%',
    height: '100%'
}));
const MapContainer = styled.div(({ theme }) => ({
    borderRadius: theme.radii[3],
    overflow: 'hidden',
    boxShadow: theme.shadows.primary[1],
    cursor: 'pointer',
    '& img': {
        width: '100%',
        height: '100%',
        objectFit: 'cover'
    }
}));

    async function __Next_Translate__getStaticProps__1981803e47b__(ctx) {
      
      return {
        
        
        props: {
          
          ...(await __loadNamespaces({
            ...ctx,
            ...__i18nConfig,
            pathname: 'library//Home/Sidebar',
            loaderName: 'getStaticProps',
            loadLocaleFrom: __i18nConfig && __i18nConfig.loadLocaleFrom || ((l, n) => import(`@next-translate-root/locales/${l}/${n}`).then(m => m.default)),
          }))
        }
      }
    }
    export { __Next_Translate__getStaticProps__1981803e47b__ as getStaticProps }
  